import { lazy, Suspense } from 'react';
import { Link, NavLink, Route, Routes } from 'react-router';
import { UserMenu } from './components/UserMenu';
import { useAuth } from './hooks/useAuth';
import { BarPage } from './pages/BarPage';
import { BookPage } from './pages/BookPage';
import { BottlePage } from './pages/BottlePage';
import { CategoriesPage } from './pages/CategoriesPage';
import { CollectionPage } from './pages/CollectionPage';
import { FriendBarPage } from './pages/FriendBarPage';
import { FriendCollectionPage } from './pages/FriendCollectionPage';
import { FriendsPage } from './pages/FriendsPage';
import { HomePage } from './pages/HomePage';
import { HouseholdPage } from './pages/HouseholdPage';
import { SignInPage } from './pages/SignInPage';

// The scanner pulls in html5-qrcode's barcode decoder (~400 kB), so load it only on the scan route.
const ScanPage = lazy(() => import('./pages/ScanPage').then((module) => ({ default: module.ScanPage })));

function NotFoundPage() {
  return (
    <section className="page-status">
      <h1>Page not found</h1>
      <Link className="btn btn--primary" to="/">
        Go home
      </Link>
    </section>
  );
}

export function App() {
  const { state, retry, signOut } = useAuth();

  if (state.status === 'loading') return <p className="page-status page-status--centered">Loading…</p>;
  if (state.status === 'signed-out') return <SignInPage />;
  if (state.status === 'error') {
    return (
      <div className="page-status page-status--centered" role="alert">
        <p className="error-text">{state.message}</p>
        <button type="button" className="btn btn--primary" onClick={retry}>
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <Link to="/" className="app-header__brand">
          <img className="app-header__mark" src="/favicon.svg" alt="" />
          <span className="app-header__name">My Catalogue</span>
        </Link>
        <nav className="app-header__nav" aria-label="Main">
          <NavLink to="/scan">Scan</NavLink>
          <NavLink to="/collection">Books</NavLink>
          <NavLink to="/bar">Bar</NavLink>
          <NavLink to="/friends">Friends</NavLink>
          <UserMenu user={state.user} onSignOut={() => void signOut()} />
        </nav>
      </header>
      <main className="app-main">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route
            path="/scan"
            element={
              <Suspense fallback={<p className="page-status">Loading scanner…</p>}>
                <ScanPage />
              </Suspense>
            }
          />
          <Route path="/household" element={<HouseholdPage />} />
          <Route path="/collection" element={<CollectionPage />} />
          <Route path="/books/:isbn" element={<BookPage />} />
          <Route path="/bar" element={<BarPage />} />
          <Route path="/bar/:id" element={<BottlePage />} />
          <Route path="/categories" element={<CategoriesPage />} />
          <Route path="/friends" element={<FriendsPage />} />
          <Route path="/friends/:id" element={<FriendCollectionPage />} />
          <Route path="/friends/:id/bar" element={<FriendBarPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
    </div>
  );
}
